var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo } from 'react';
import { FormProvider, useController, useForm, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, useAnimationControls } from 'framer-motion';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import { deliveryDareVideoSchemaZ } from 'root/src/shared/descriptions/formSchemas/deliveryDareVideoSchema';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { DarePlatform } from 'root/src/shared/@types/platforms';
import { getReadableDarePlatform } from 'root/src/shared/platforms/helpers/getReadablePlatform';
import { getTimestampFromVideoURL } from 'root/src/client/logic/project/thunks/deliveryDare';
import { getErrorMessage } from 'root/src/client/v2/common/hooks/useDareDropApi';
import { isApiAuthTRPCError } from 'root/src/shared/error/guards/isApiAuthError.guard';
import { getReadableProviderName } from 'root/src/client/v2/auth/helpers/providerHelpers';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { DarePlatformIcon, PlatformIconColor } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import { Field } from 'root/src/client/v2/common/components/ui/field';
import { Input } from 'root/src/client/v2/common/components/ui/input';
import { Textarea } from 'root/src/client/v2/common/components/ui/textarea';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
import { joinWithOr } from 'root/src/client/v2/common/helpers/joinWithOr';
import { cn } from 'root/src/client/v2/common/helpers/cn';
// Short messages for the inline field error + the toast (the schema's union error is very
// long and would grow the modal / shift the layout).
const VIDEO_URL_MSG = 'Use a video link from Twitch, YouTube, Facebook, X or TikTok.';
const NOTES_MSG = 'Notes must be under 500 characters.';
const SHAKE = { x: [0, -5, 5, -5, 5, 0], transition: { duration: 0.35 } };
// Names the delivery surface the way the server validates it. Twitch reads as "Twitch clips" on a
// clips-only dare because that is the exact shape the Twitch strategy accepts: a VOD on the same
// platform is rejected (server/modules/dares/services/validator/strategies/twitch.strategy.ts).
const deliveryPlatformLabel = (platform, clipsOnly) => (platform === DarePlatform.Twitch && clipsOnly ? 'Twitch clips' : getReadableDarePlatform(platform));
// The creator is told the rule before they paste a link, rather than discovering it from the rejection
// toast after delivering. Undefined when the dare's platforms are unknown, so the notice stays off
// instead of showing an empty or misleading constraint.
const getDeliveryRequirement = (platforms, clipsOnly) => {
    const labels = (platforms !== null && platforms !== void 0 ? platforms : []).map(platform => deliveryPlatformLabel(platform, clipsOnly)).filter(Boolean);
    if (labels.length === 0) {
        return undefined;
    }
    const clipsNote = clipsOnly && (platforms === null || platforms === void 0 ? void 0 : platforms.includes(DarePlatform.Twitch)) ? ' A link to a Twitch VOD will be rejected.' : '';
    return `You can only deliver on ${joinWithOr(labels)}.${clipsNote}`;
};
// Amber is reserved for the clips-only case: it is the restriction creators actually get rejected on,
// so it keeps the emphasis it had as a standalone warning. Every other dare states its platforms in a
// neutral panel, which would lose its meaning if everything shouted.
const DeliveryRequirementNotice = ({ platforms, clipsOnly }) => {
    const requirement = getDeliveryRequirement(platforms, clipsOnly);
    if (!requirement) {
        return null;
    }
    const isClipsOnly = Boolean(clipsOnly) && Boolean(platforms === null || platforms === void 0 ? void 0 : platforms.includes(DarePlatform.Twitch));
    return (<div className={cn('flex w-full items-center gap-2.5 rounded-lg border border-solid p-3 text-left text-xs', isClipsOnly
            ? 'border-neon-amber/30 bg-neon-amber/10 text-neon-amber'
            : 'border-white/[0.08] bg-white/[0.03] text-muted-foreground')}>
			{isClipsOnly
            ? <ContentCutIcon sx={{ fontSize: 14 }} className="shrink-0"/>
            : (<ul className="flex shrink-0 items-center gap-1.5">
						{platforms === null || platforms === void 0 ? void 0 : platforms.map(platform => (<li key={platform} className="flex h-5 w-5 items-center justify-center rounded-full bg-white">
								<DarePlatformIcon size="12" platform={platform} color={PlatformIconColor.Dark}/>
							</li>))}
					</ul>)}
			<p className="flex-1">{requirement}</p>
		</div>);
};
const VideoURLField = ({ shake }) => {
    const form = useFormContext();
    const { field, fieldState } = useController({ name: 'videoURL', control: form.control });
    return (<motion.div animate={shake}>
			<Field label="Video URL" htmlFor="dd-video" error={fieldState.error ? VIDEO_URL_MSG : undefined}>
				<Input id="dd-video" placeholder="Link to your stream VOD or clip" error={Boolean(fieldState.error)} {...field}/>
			</Field>
		</motion.div>);
};
const NotesField = ({ shake }) => {
    var _a;
    const form = useFormContext();
    const { field, fieldState } = useController({ name: 'notesForReviewer', control: form.control });
    return (<motion.div animate={shake}>
			<Field label="Notes for the reviewer (optional)" htmlFor="dd-notes" error={fieldState.error ? NOTES_MSG : undefined}>
				<Textarea id="dd-notes" rows={3} placeholder="Anything the reviewer should know" error={Boolean(fieldState.error)} {...field} value={(_a = field.value) !== null && _a !== void 0 ? _a : ''}/>
			</Field>
		</motion.div>);
};
export const RedesignDeliverDareModal = memo(({ projectUrl, platforms, clipsOnly, onSuccess }) => {
    const modal = useRedesignModal();
    const { loading, success, error, run } = useAsyncAction();
    const deliverMutation = trpc.dares.deliver.useMutation();
    const videoShake = useAnimationControls();
    const notesShake = useAnimationControls();
    const form = useForm({
        defaultValues: { videoURL: '', notesForReviewer: '' },
        resolver: zodResolver(deliveryDareVideoSchemaZ),
    });
    const onSubmit = form.handleSubmit((data) => {
        void run(() => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            yield deliverMutation.mutateAsync({
                projectUrl,
                videoURL: data.videoURL,
                notesForReviewer: (_a = data.notesForReviewer) !== null && _a !== void 0 ? _a : undefined,
                timeStamp: getTimestampFromVideoURL(data.videoURL),
            });
            yield (onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess());
        }), {
            onSuccess: modal.hide,
            toast: {
                loading: 'Delivering dare...',
                success: 'Dare delivered successfully!',
                error: caught => (isApiAuthTRPCError(caught)
                    ? `Please re-authenticate your ${getReadableProviderName(caught.data.props.provider)} account, then deliver again.`
                    : getErrorMessage(caught)),
            },
        });
    }, (errors) => {
        // Inline short message + toast + shake the bad field (no long union error that grows the modal).
        if (errors.videoURL) {
            void videoShake.start(SHAKE);
            toast.error(VIDEO_URL_MSG);
        }
        else if (errors.notesForReviewer) {
            void notesShake.start(SHAKE);
            toast.error(NOTES_MSG);
        }
    });
    return (<RedesignModalShell className="sm:max-w-lg">
			<div className="flex w-full flex-col items-center gap-1 text-center">
				<h2 className="font-display text-xl font-bold text-foreground">Deliver Dare</h2>
				<p className="text-sm text-muted-foreground">Submit the moment you completed the dare for review.</p>
			</div>
			<DeliveryRequirementNotice platforms={platforms} clipsOnly={clipsOnly}/>
			<FormProvider {...form}>
				<form onSubmit={onSubmit} className="flex w-full flex-col gap-5">
					<VideoURLField shake={videoShake}/>
					<NotesField shake={notesShake}/>
					<Button type="submit" variant="brand" loading={loading} success={success} error={error} className="w-full rounded-lg text-sm font-semibold">
						Deliver
					</Button>
				</form>
			</FormProvider>
		</RedesignModalShell>);
});
